// Historique SCÉNARISÉ d'un personnage — CCT-97 (centre de messagerie).
//
// Le centre de messagerie raisonne par interlocuteur : la chronologie d'un personnage doit
// contenir CE QUI A ÉTÉ ÉCHANGÉ avec lui dans la mission (chats du scénario), pas seulement
// les fils ouverts par le moteur. On reconstruit donc les messages depuis le scénario joué.
//
// Trois sources, par ordre de fidélité :
//   1. le fil PERSISTÉ en base (assignment.chatstatejson) : survit au rechargement ;
//   2. l'état de chat en session (ChatStateContext), si la base n'a pas encore été écrite ;
//   3. à défaut, le script de l'élément S'IL EST COMPLÉTÉ (progression persistée en base) —
//      dans ce cas seules les répliques du personnage sont connues, pas celles du joueur.
// Un chat ni joué ni complété est VERROUILLÉ : on n'en montre rien.

import { getScenarioForMission } from '../data/scenarios';
import { buildChatStateKey, type ChatState } from '../context/ChatStateContext';
import type { PersistedChatMsg } from '../api/storyChat';

export interface ScriptedMsg {
  role: 'character' | 'student';
  text: string;
  /** Locuteur réel : un chat de scénario peut faire parler PLUSIEURS personnages. */
  speaker?: string;
  /** Élément de séquence d'origine (chat du chapitre). */
  itemId: string;
  itemTitle: string;
}

const norm = (s: string) => s.trim().toLowerCase();
/** Même personne malgré « Priya » vs « Priya Chandran » : on compare aussi le prénom. */
const sameCharacter = (a: string, b: string) =>
  !!a && !!b && (norm(a) === norm(b) || norm(a).split(' ')[0] === norm(b).split(' ')[0]);

export function scriptedHistoryFor(
  missionId: number,
  characterName: string | null | undefined,
  deps: {
    completed: Set<string>;
    getState: (key: string) => ChatState | undefined;
    /** Fils persistés en base, par id d'élément — source de vérité après rechargement. */
    persisted?: Record<string, PersistedChatMsg[]>;
  },
): ScriptedMsg[] {
  if (!characterName) return [];
  const scenario: any = getScenarioForMission(missionId);
  if (!scenario?.chapters?.length) return [];

  const chars: any[] = scenario.characters ?? [];
  const nameOf = (cid: any): string => {
    if (typeof cid !== 'string') return '';
    return chars.find(c => c.id === cid || c.name === cid)?.name ?? cid;
  };

  const out: ScriptedMsg[] = [];
  for (const chapter of scenario.chapters) {
    for (const item of chapter?.sequence ?? []) {
      if (item?.type !== 'chat') continue;
      const content: any = item.content ?? {};
      const msgs: any[] = Array.isArray(content.messages) ? content.messages : [];
      const speakers = [content.tutorCharacterId, ...msgs.map(m => m?.characterId)].map(nameOf);
      if (!speakers.some(n => sameCharacter(n, characterName))) continue;

      const itemId = String(item.id ?? '');
      const itemTitle = String(item.title ?? 'Discussion');
      const tutorName = nameOf(content.tutorCharacterId);
      const saved = deps.getState(buildChatStateKey(missionId, itemId));
      const stored = deps.persisted?.[itemId];

      // Base d'abord : c'est la SEULE source qui survit au rechargement (répliques du joueur).
      if (stored?.length) {
        // Tolérance aux fils déjà écrits avant le correctif : on ignore un doublon consécutif.
        const dedup = stored.filter((m, i) =>
          i === 0 || m.role !== stored[i - 1].role || m.text !== stored[i - 1].text);
        for (const m of dedup) {
          if (m.role === 'student') { out.push({ role: 'student', text: m.text, itemId, itemTitle }); continue; }
          const who = m.characterName || tutorName;
          if (who && !sameCharacter(who, characterName)) continue;
          out.push({ role: 'character', text: m.text, speaker: who, itemId, itemTitle });
        }
        continue;
      }

      if (saved?.chatItems?.length) {
        // L'échange réel : on garde les messages du personnage visé et les réponses du joueur.
        for (const entry of saved.chatItems) {
          if (entry.kind === 'msg') {
            const d = entry.data;
            if (d.sender === 'user') { out.push({ role: 'student', text: d.text, itemId, itemTitle }); continue; }
            if (!d.characterName || sameCharacter(d.characterName, characterName)) {
              out.push({ role: 'character', text: d.text, speaker: d.characterName || tutorName, itemId, itemTitle });
            }
          } else if (entry.kind === 'answered') {
            out.push({ role: 'character', text: entry.data.question.text, itemId, itemTitle });
            const a = entry.data.selectedAnswer;
            if (a) out.push({ role: 'student', text: Array.isArray(a) ? a.join(', ') : a, itemId, itemTitle });
          } else if (entry.kind === 'choice-turn' && entry.data.selectedLabel) {
            out.push({ role: 'student', text: entry.data.selectedLabel, itemId, itemTitle });
          }
        }
        continue;
      }

      // Pas d'état en session : on ne rejoue le script QUE si l'élément est complété — sinon
      // on divulguerait une conversation que le joueur n'a pas encore eue.
      if (!deps.completed.has(itemId)) continue;
      for (const m of msgs) {
        const from = nameOf(m?.characterId) || tutorName;
        if (!sameCharacter(from, characterName)) continue;
        const text = typeof m?.text === 'string' ? m.text : '';
        if (text) out.push({ role: 'character', text, speaker: from, itemId, itemTitle });
      }
    }
  }
  return out;
}
